import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Typography, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Chip, CircularProgress,
  Alert, Button, Tooltip, useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon      from '@mui/icons-material/Save';
import FeedbackWidget from '../../components/director/FeedbackWidget';
import {
  useGetDirectorEventDetailQuery,
  useGetDirectorFeedbackQuery,
  useSaveDirectorFeedbackMutation,
} from '../../redux/services/director-api';
import ScoreDisplay from '../../components/director/ScoreDisplay';
import { getRubricSemaforo, getScoreConfig, showsNumber } from '../../utils/director-score';

// ── Hooks para manejar feedback local antes de guardar ─────────────────────────
function useFeedbackState(feedbacks) {
  const [localFeedback, setLocalFeedback] = useState({});

  useEffect(() => {
    if (!feedbacks) return;
    const map = {};
    feedbacks.forEach(fb => {
      const key = fb.rubric ?? 'event';
      map[key] = { stars: fb.stars, comment: fb.comment, commentType: fb.commentType };
    });
    setLocalFeedback(map);
  }, [feedbacks]);

  const getFor = (key) => localFeedback[key] ?? { stars: 0, comment: '', commentType: null };

  const setFor = (key, field, value) =>
    setLocalFeedback(prev => ({
      ...prev,
      [key]: { ...getFor(key), ...prev[key], [field]: value },
    }));

  return { getFor, setFor, localFeedback };
}

// ── Page ───────────────────────────────────────────────────────────────────────
export default function DirectorEventDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const { data: detail, isLoading: loadingDetail, isError: errorDetail } = useGetDirectorEventDetailQuery(id);
  const { data: feedbacks, isLoading: loadingFeedback } = useGetDirectorFeedbackQuery(id);
  const [saveFeedback, { isLoading: saving }] = useSaveDirectorFeedbackMutation();

  const { getFor, setFor, localFeedback } = useFeedbackState(feedbacks);

  const [saveError, setSaveError]     = useState(null);
  const [saveSuccess, setSaveSuccess] = useState(false);

  const handleSave = async () => {
    setSaveError(null);
    setSaveSuccess(false);

    const entries = Object.entries(localFeedback).filter(([, v]) => v.stars > 0);
    if (!entries.length) return;

    try {
      for (const [key, value] of entries) {
        await saveFeedback({
          eventId:     id,
          rubricId:    key === 'event' ? undefined : key,
          stars:       value.stars,
          comment:     value.comment,
          commentType: value.commentType,
        }).unwrap();
      }
      setSaveSuccess(true);
    } catch (err) {
      setSaveError(err?.data?.message ?? 'Error al guardar el feedback.');
    }
  };

  if (loadingDetail || loadingFeedback) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', pt: 8 }}>
        <CircularProgress sx={{ color: '#284B32' }} />
      </Box>
    );
  }

  if (errorDetail) {
    return <Alert severity="error" sx={{ m: 2 }}>No se pudo cargar el evento.</Alert>;
  }

  const { event, rubrics } = detail;
  const eventFb = getFor('event');

  // Configuración de visualización del campamento (semáforo / puntuación / ambos)
  const { mode: scoreMode, format: scoreFormat } = getScoreConfig(detail.activeCamp);
  const scoreColumnLabel = showsNumber(scoreMode) ? 'Calificación' : 'Calificación en Colores';

  return (
    <Box>
      {/* ── Header verde ── */}
      <Paper
        elevation={0}
        sx={{
          backgroundColor: '#1E3A2F',
          color: '#fff',
          px: 3,
          py: 2,
          mb: 3,
          borderRadius: 1,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Tooltip title="Volver al dashboard">
            <Button
              size="small"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/director/dashboard')}
              sx={{ color: 'rgba(255,255,255,0.7)', textTransform: 'none', minWidth: 0 }}
            >
              Volver
            </Button>
          </Tooltip>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.25 }}>
              {event.name}
            </Typography>
            {detail.judgeInfo && (
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)', display: 'block' }}>
                Juez: {detail.judgeInfo.name}
                {' · '}
                Sync: {new Date(detail.judgeInfo.syncedAt).toLocaleString('es', {
                  dateStyle: 'short', timeStyle: 'short',
                })}
              </Typography>
            )}
          </Box>
          <ScoreDisplay
            semaforo={detail.overallSemaforo}
            score={detail.overallScore}
            maxPoints={detail.overallMaxPoints}
            mode={scoreMode}
            format={scoreFormat}
          />
        </Box>
      </Paper>

      {/* ── Feedback del evento completo ── */}
      <Paper variant="outlined" sx={{ p: 2.5, mb: 3 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#284B32', mb: 1.5 }}>
          Tu valoración del evento
        </Typography>
        <FeedbackWidget
          stars={eventFb.stars}
          onStarsChange={(v) => setFor('event', 'stars', v)}
          comment={eventFb.comment}
          onCommentChange={(v) => setFor('event', 'comment', v)}
          commentType={eventFb.commentType}
          onCommentTypeChange={(v) => setFor('event', 'commentType', v)}
          hasExistingNote={!!feedbacks?.find(f => !f.rubric)?.comment}
        />
      </Paper>

      {/* ── Tabla de rúbricas ── */}
      <Paper variant="outlined" sx={{ mb: 3 }}>
        <Box sx={{ px: 2.5, py: 1.5, backgroundColor: '#284B32', borderRadius: '4px 4px 0 0' }}>
          <Typography variant="subtitle2" sx={{ color: '#fff', fontWeight: 600 }}>
            Rúbricas
          </Typography>
        </Box>
        {isMobile ? (
          <Box sx={{ p: 1.5 }}>
            {rubrics.map((row, idx) => {
              const fb      = getFor(row.rubric._id);
              const canFb   = row.isGraded && row.synced;
              const existFb = feedbacks?.find(f => f.rubric === row.rubric._id);

              return (
                <Paper
                  key={row.rubric._id}
                  variant="outlined"
                  sx={{
                    p: 1.5,
                    mb: idx === rubrics.length - 1 ? 0 : 1.25,
                    borderColor: 'rgba(40,75,50,0.14)',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {row.rubric.name}
                    </Typography>
                    <ScoreDisplay
                      semaforo={getRubricSemaforo(row)}
                      score={row.grade}
                      maxPoints={row.rubric.maxPoints}
                      mode={scoreMode}
                      format={scoreFormat}
                      compact
                    />
                  </Box>

                  {row.savedBy && (
                    <Typography variant="caption" color="#1D4ED8" sx={{ display: 'block', mb: 1 }}>
                      Juez: {row.savedBy.name}
                    </Typography>
                  )}

                  {!canFb ? (
                    <Typography variant="caption" color="text.disabled">
                      Sin feedback disponible
                    </Typography>
                  ) : (
                    <FeedbackWidget
                      stars={fb.stars}
                      onStarsChange={(v) => setFor(row.rubric._id, 'stars', v)}
                      comment={fb.comment}
                      onCommentChange={(v) => setFor(row.rubric._id, 'comment', v)}
                      commentType={fb.commentType}
                      onCommentTypeChange={(v) => setFor(row.rubric._id, 'commentType', v)}
                      hasExistingNote={!!existFb?.comment}
                    />
                  )}
                </Paper>
              );
            })}
          </Box>
        ) : (
          <TableContainer sx={{ overflowX: 'auto' }}>
            <br/>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: 'rgba(40,75,50,0.06)' }}>
                  {['Rúbrica', scoreColumnLabel, 'Tu feedback'].map(h => (
                    <TableCell key={h} sx={{ fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary', py: 1 }}>
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {rubrics.map((row, idx) => {
                  const fb      = getFor(row.rubric._id);
                  const canFb   = row.isGraded && row.synced;
                  const existFb = feedbacks?.find(f => f.rubric === row.rubric._id);

                  return (
                    <TableRow
                      key={row.rubric._id}
                      sx={{ verticalAlign: 'top', backgroundColor: idx % 2 === 0 ? 'transparent' : 'rgba(0,0,0,0.015)' }}
                    >
                      <TableCell sx={{ py: 1.5 }}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>{row.rubric.name}</Typography>
                        {row.savedBy && (
                          <Typography variant="caption" color="#1D4ED8" sx={{ display: 'block' }}>
                            Juez: {row.savedBy.name}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ py: 1.5 }}>
                        <ScoreDisplay
                          semaforo={getRubricSemaforo(row)}
                          score={row.grade}
                          maxPoints={row.rubric.maxPoints}
                          mode={scoreMode}
                          format={scoreFormat}
                        />
                      </TableCell>
                      <TableCell sx={{ py: 1.5, minWidth: 280 }}>
                        {!canFb ? (
                          <Typography variant="caption" color="text.disabled">
                            Sin feedback disponible
                          </Typography>
                        ) : (
                          <FeedbackWidget
                            stars={fb.stars}
                            onStarsChange={(v) => setFor(row.rubric._id, 'stars', v)}
                            comment={fb.comment}
                            onCommentChange={(v) => setFor(row.rubric._id, 'comment', v)}
                            commentType={fb.commentType}
                            onCommentTypeChange={(v) => setFor(row.rubric._id, 'commentType', v)}
                            hasExistingNote={!!existFb?.comment}
                          />
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* ── Mensajes de estado ── */}
      {saveError   && <Alert severity="error"   sx={{ mb: 2 }}>{saveError}</Alert>}
      {saveSuccess && <Alert severity="success" sx={{ mb: 2 }}>Feedback guardado correctamente.</Alert>}

      {/* ── Guardar ── */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant="contained"
          startIcon={<SaveIcon />}
          onClick={handleSave}
          disabled={saving}
          sx={{
            backgroundColor: '#0F6E56',
            '&:hover': { backgroundColor: '#0a5a45' },
            textTransform: 'none',
            fontWeight: 600,
          }}
        >
          {saving ? 'Guardando…' : 'Guardar todo'}
        </Button>
      </Box>
    </Box>
  );
}
